import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputLabel,
  FormControl,
  useMediaQuery,
  Collapse,
  Divider,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EmailIcon from "@mui/icons-material/Email";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SecurityIcon from "@mui/icons-material/Security";
import FlagIcon from "@mui/icons-material/Flag";
import BuildIcon from "@mui/icons-material/Build";
import FolderIcon from "@mui/icons-material/Folder";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import DashboardIcon from "@mui/icons-material/Dashboard";

import OwnerDashboardSettings from "./OwnerDashboardSettings";
import HOADocuments from "./HOADocuments";
import Statuses from "./Statuses";
import Company from "./Company";
import Uploads from "./Uploads";
import Roles from "./Roles";
import WorkOrderManager from "./WorkOrderManager";
import ArchitecturalRequestManager from "./ArchitecturalRequestManager";
import ViolationstManager from "./ViolationManager";
import NotificationLetter from "./Coverletters";
import PriorityLevels from "./PriorityLevels";
import Notifications from "./EmailNotifications";
import EmailTemplate from "./EmailTemplate";
import IntegrationSettings from "./IntegrationSettings";
import api from "../../utils/api";

const tabList = [
  { id: 0, label: "General", icon: <SettingsIcon /> },
  { id: 1, label: "Notifications", icon: <NotificationsActiveIcon /> },
  { id: 2, label: "Email Configuration", icon: <EmailIcon /> },
  { id: 3, label: "Uploads", icon: <CloudUploadIcon /> },
  { id: 4, label: "Statuses", icon: <ListAltIcon /> },
  { id: 5, label: "Roles", icon: <SecurityIcon /> },
  { id: 6, label: "Priority Levels", icon: <FlagIcon /> },
  { id: 7, label: "Email Templates", icon: <MoveToInboxIcon /> },
  { id: 8, label: "Integration Settings", icon: <IntegrationInstructionsIcon /> },
  { id: 9, label: "HOA Documents", icon: <FolderIcon /> },
  { id: 10, label: "Notification Letter", icon: <FolderIcon /> },
  { id: 11, label: "Owner Dashboard Settings", icon: <DashboardIcon /> },
];

const typeManagerTabs = [
  { id: 100, label: "Work Order Manager", icon: <BuildIcon /> },
  { id: 101, label: "Architectural Request Manager", icon: <BuildIcon /> },
  { id: 102, label: "Violations Manager", icon: <BuildIcon /> },
];

const SystemAdministration = () => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [typesManagerOpen, setTypesManagerOpen] = useState(true);
  const [tabIndex, setTabIndex] = useState(
    () => parseInt(localStorage.getItem("adminTabIndex"), 10) || 0
  );
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [testResult, setTestResult] = useState("");

  const [settings, setSettings] = useState({
    smtpServer: "",
    smtpPort: "587",
    smtpUsername: "",
    smtpPassword: "",
    smtpAuth: true,
    smtpSSL: true,
    fromEmail: "",
    emailFormat: "PDF",
    testRecipient: "",
  });

  const sortedTabList = useMemo(
    () => [...tabList].sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  const sortedTypeManagerTabs = useMemo(
    () => [...typeManagerTabs].sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  useEffect(() => {
    localStorage.setItem("adminTabIndex", String(tabIndex));
  }, [tabIndex]);

  useEffect(() => {
    api
      .get(`/smtp-settings`)
      .then((res) => {
        if (res.data) {
          setSettings({
            smtpServer: res.data.smtp_server || "",
            smtpPort: res.data.smtp_port || "587",
            smtpUsername: res.data.smtp_username || "",
            smtpPassword: res.data.smtp_password || "",
            smtpAuth: res.data.smtp_auth ?? true,
            smtpSSL: res.data.smtp_ssl ?? false,
            fromEmail: res.data.from_email || "",
            emailFormat: res.data.email_format || "PDF",
            testRecipient: res.data.test_recipient || "",
          });
        }
      })
      .catch(() => setSaveError(true));
  }, []);

  const handleChange = (e) =>
    setSettings({ ...settings, [e.target.name]: e.target.value });

  const handleToggle = (e) =>
    setSettings({ ...settings, [e.target.name]: e.target.checked });

  const handleSave = async () => {
    try {
      await api.post(`/smtp-settings`, settings);
      setSaveSuccess(true);
    } catch {
      setSaveError(true);
    }
  };

  const testSMTPConnection = async () => {
    setLoading(true);
    setTestResult("");
    try {
      const response = await api.post(`/test-smtp`, {
        ...settings,
        toEmail: settings.testRecipient,
      });
      setTestResult(`✅ ${response.data.message}`);
    } catch (error) {
      setTestResult(
        `❌ SMTP Test Failed: ${error.response?.data?.message || error.message}`
      );
    }
    setLoading(false);
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    borderColor: "rgba(47,99,173,0.25)",
    color: "#2f63ad",
    px: 2.25,
  };

  const settingsSectionSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
    backgroundColor: "rgba(255,255,255,0.96)",
    p: { xs: 2, md: 2.5 },
  };

  const switchRowSx = {
    mt: 1.5,
    px: 1.25,
    py: 1.1,
    borderRadius: "12px",
    backgroundColor: "rgba(47,99,173,0.04)",
    border: "1px solid rgba(47,99,173,0.08)",
  };

  const renderEmailConfiguration = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          Email Configuration
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Configure SMTP delivery, sender settings, and send a test email.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 2.5 }}>
        <Paper elevation={0} sx={settingsSectionSx}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            SMTP Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Enter your outgoing mail server details.
          </Typography>

          <TextField
            fullWidth
            label="SMTP Server"
            name="smtpServer"
            value={settings.smtpServer}
            onChange={handleChange}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="SMTP Port"
            name="smtpPort"
            value={settings.smtpPort}
            onChange={handleChange}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="User Name"
            name="smtpUsername"
            value={settings.smtpUsername}
            onChange={handleChange}
            sx={fieldSx}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            name="smtpPassword"
            value={settings.smtpPassword}
            onChange={handleChange}
            sx={fieldSx}
          />

          <Box sx={switchRowSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smtpAuth}
                  onChange={handleToggle}
                  name="smtpAuth"
                />
              }
              label="SMTP server requires authentication"
              sx={{ m: 0 }}
            />
          </Box>

          <Box sx={switchRowSx}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.smtpSSL}
                  onChange={handleToggle}
                  name="smtpSSL"
                />
              }
              label="Enable SSL"
              sx={{ m: 0 }}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={settingsSectionSx}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Email Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            Control sender identity, message format, and test delivery.
          </Typography>

          <TextField
            fullWidth
            label="From Email"
            name="fromEmail"
            value={settings.fromEmail}
            onChange={handleChange}
            sx={fieldSx}
          />

          <FormControl fullWidth sx={fieldSx}>
            <InputLabel>Email Format</InputLabel>
            <Select
              name="emailFormat"
              value={settings.emailFormat}
              label="Email Format"
              onChange={handleChange}
            >
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="HTML">HTML</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Test Recipient Email"
            name="testRecipient"
            value={settings.testRecipient}
            onChange={handleChange}
            sx={fieldSx}
          />

          <Divider sx={{ my: 2.5 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={testSMTPConnection}
              disabled={loading}
              startIcon={loading ? null : <SendIcon />}
              sx={secondaryButtonSx}
            >
              {loading ? <CircularProgress size={20} /> : "Send Test Email"}
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              sx={primaryButtonSx}
            >
              Save Settings
            </Button>
          </Box>

          {testResult && (
            <Box
              sx={{
                mt: 2,
                px: 1.5,
                py: 1.25,
                borderRadius: "12px",
                backgroundColor: testResult.includes("✅")
                  ? "rgba(46,125,50,0.08)"
                  : "rgba(211,47,47,0.08)",
                border: testResult.includes("✅")
                  ? "1px solid rgba(46,125,50,0.18)"
                  : "1px solid rgba(211,47,47,0.18)",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: testResult.includes("✅") ? "#2e7d32" : "#d32f2f",
                  fontWeight: 600,
                }}
              >
                {testResult}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );

  const renderTabContent = () => {
    switch (tabIndex) {
      case 0:
        return <Company />;
      case 1:
        return <Notifications />;
      case 2:
        return renderEmailConfiguration();
      case 3:
        return <Uploads />;
      case 4:
        return <Statuses />;
      case 5:
        return <Roles />;
      case 6:
        return <PriorityLevels />;
      case 7:
        return <EmailTemplate />;
      case 8:
        return <IntegrationSettings />;
      case 9:
        return <HOADocuments />;
      case 10:
        return <NotificationLetter />;
      case 11:
        return <OwnerDashboardSettings />;  
      case 100:
        return <WorkOrderManager />;
      case 101:
        return <ArchitecturalRequestManager />;
      case 102:
        return <ViolationstManager />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        minHeight: "100vh",
        p: { xs: 1.5, md: 2.5 },
        gap: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: isMobile ? "100%" : 280,
          borderRadius: "18px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          overflow: "hidden",
          alignSelf: "flex-start",
          backgroundColor: "rgba(255,255,255,0.96)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            borderBottom: "1px solid #eef2f7",
            backgroundColor: "#fbfcfe",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            Administration
          </Typography>
        </Box>

        <List sx={{ py: 1 }}>
          {sortedTabList.map((tab) => (
            <ListItemButton
              key={tab.id}
              selected={tabIndex === tab.id}
              onClick={() => setTabIndex(tab.id)}
              sx={{
                mx: 1,
                my: 0.25,
                borderRadius: "12px",
                "&.Mui-selected": {
                  backgroundColor: "rgba(47,99,173,0.10)",
                  color: "#2f63ad",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(47,99,173,0.16)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 38,
                  color: tabIndex === tab.id ? "#2f63ad" : "#5f6b7a",
                }}
              >
                {tab.icon}
              </ListItemIcon>
              <ListItemText
                primary={tab.label}
                primaryTypographyProps={{
                  fontWeight: tabIndex === tab.id ? 700 : 500,
                }}
              />
            </ListItemButton>
          ))}

          <ListItemButton
            onClick={() => setTypesManagerOpen(!typesManagerOpen)}
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: "12px",
            }}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>
              <BuildIcon />
            </ListItemIcon>
            <ListItemText
              primary="Types Manager"
              primaryTypographyProps={{ fontWeight: 600 }}
            />
            {typesManagerOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={typesManagerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {sortedTypeManagerTabs.map((item) => (
                <ListItemButton
                  key={item.id}
                  sx={{
                    pl: 4,
                    mx: 1,
                    my: 0.25,
                    borderRadius: "12px",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(47,99,173,0.10)",
                      color: "#2f63ad",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(47,99,173,0.16)",
                    },
                  }}
                  selected={tabIndex === item.id}
                  onClick={() => setTabIndex(item.id)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 34,
                      color: tabIndex === item.id ? "#2f63ad" : "#5f6b7a",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: tabIndex === item.id ? 700 : 500,
                      fontSize: "0.95rem",
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </List>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          borderRadius: "18px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          minWidth: 0,
          backgroundColor: "rgba(255,255,255,0.96)",
        }}
      >
        {renderTabContent()}
      </Paper>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Settings saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={saveError}
        autoHideDuration={3000}
        onClose={() => setSaveError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSaveError(false)}>
          Failed to save settings.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemAdministration;
